var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var _a;
import { AggregatedLoadingObservable, assert, assertDefined, augmentErrorMessage, compareArrays, count, Observable, toLengthUnit, } from "@configura/web-utilities";
import { CfgMeasureDefinition } from "./CfgMeasure.js";
import { ProductConfigurationBubbleMode } from "./productConfiguration/CfgOption.js";
import { CfgProductConfiguration } from "./productConfiguration/CfgProductConfiguration.js";
import { collectAdditionalProductRefs } from "./productConfiguration/utilitiesProductConfiguration.js";
import { wrapWithCache } from "./productLoader.js";
import { SyncGroupsHandler } from "./syncGroups/SyncGroupsHandler.js";
import { compareCfgProductData, correctDefaultsOnCatalogueParams, isSameCatalogueParams, isSameProductRef, makeProductKey, } from "./utilitiesCatalogueData.js";
import { CfgProdConfParts, convertDtoProductConfToV1, isAdditionalProductConfiguration, isProductConf, } from "./utilitiesConfiguration.js";
function completeSettings(incompleteSettings) {
    var _b, _c;
    return {
        strictSelectOneSelectionCount: (_b = incompleteSettings === null || incompleteSettings === void 0 ? void 0 : incompleteSettings.strictSelectOneSelectionCount) !== null && _b !== void 0 ? _b : false,
        syncGroupsApplyMode: incompleteSettings === null || incompleteSettings === void 0 ? void 0 : incompleteSettings.syncGroupsApplyMode,
        disableMatchOnDescription: (_c = incompleteSettings === null || incompleteSettings === void 0 ? void 0 : incompleteSettings.disableMatchOnDescription) !== null && _c !== void 0 ? _c : false,
    };
}
/**
 * This enum is used internally in the SDK and is not expected by be used directly by integrators.
 */
export var CfgProductBubbleMode;
(function (CfgProductBubbleMode) {
    /** Stop bubbling. */
    CfgProductBubbleMode["Stop"] = "Stop";
    /**
     * Bubble to the parent CfgProduct up the tree.
     * This makes the CfgProduct we we call from notify that it has changed, and the CfgProduct
     * above switch out the reference to this.
     */
    CfgProductBubbleMode["OneLevel"] = "OneLevel";
    /** Bubble to the root CfgProduct. */
    CfgProductBubbleMode["ToRoot"] = "ToRoot";
    /** Bubble to the root CfgProduct and turn on all optional CfgProducts on the way up. */
    CfgProductBubbleMode["ToRootAndBubbleSelected"] = "ToRootAndBubbleSelected";
})(CfgProductBubbleMode || (CfgProductBubbleMode = {}));
function isDescriptionMatch(l, r) {
    const ld = l.description;
    const rd = r.description;
    return ld !== undefined && rd !== undefined && ld.toLowerCase() === rd.toLowerCase();
}
/**
 * This class is meant to only be used through CfgProduct. It should never be instantiated on its
 * own. Normally the internal state of this class should never be directly modified. CfgProduct is
 * the class that should be used and interacted with.
 */
export class _CfgProductInternal {
    constructor(initSuccess, initFail, _productLoaderRaw, prodParams, settings, optional, selected, rootFeatureRefs, rawFeatures, notes, uuid, _rawUnit, _rawProductData, apiSelection, apiConstraints, loadingObservable, parent, root, _additionalProductRef, _syncGroupHandler) {
        var _b;
        this._productLoaderRaw = _productLoaderRaw;
        this.prodParams = prodParams;
        this.settings = settings;
        this.uuid = uuid;
        this._rawUnit = _rawUnit;
        this._rawProductData = _rawProductData;
        this.loadingObservable = loadingObservable;
        this.parent = parent;
        this._additionalProductRef = _additionalProductRef;
        this._syncGroupHandler = _syncGroupHandler;
        this._destroyed = false;
        this.additionalProducts = [];
        this._notes = new Map();
        this.changeObservable = new Observable();
        /** Mark this and its descendants as destroyed and remove all listeners */
        this.destroy = () => {
            var _b;
            this._destroyed = true;
            this.changeObservable.stopAllListen();
            this.configuration.stopAllListenForChange();
            for (const additionalProduct of (_b = this.additionalProducts) !== null && _b !== void 0 ? _b : []) {
                additionalProduct.destroy();
            }
        };
        /**
         * Reset will reset the product to its initial state
         */
        this.reset = () => __awaiter(this, void 0, void 0, function* () {
            if (this._initialClone !== undefined) {
                yield this.copyFrom(this._initialClone, true);
            }
        });
        this._notifyAllOfChange = (bubbleMode, committed) => __awaiter(this, void 0, void 0, function* () {
            if (bubbleMode === CfgProductBubbleMode.Stop) {
                return;
            }
            const parent = this.parent;
            const freshRef = CfgProduct._makeNewRefFrom(this);
            this.changeObservable.notifyAll({ freshRef, committed });
            if (parent !== undefined) {
                yield parent._additionalProductHasChanged(freshRef, bubbleMode === CfgProductBubbleMode.OneLevel
                    ? CfgProductBubbleMode.Stop
                    : bubbleMode, committed);
            }
        });
        /** Called when a child (additional product or the configuration) has changed. */
        this._childHasChanged = (bubbleMode, committed) => __awaiter(this, void 0, void 0, function* () {
            if (bubbleMode === CfgProductBubbleMode.ToRootAndBubbleSelected &&
                this.optional &&
                !this.selected) {
                yield this.setSelected(true, bubbleMode, false);
                return;
            }
            yield this._notifyAllOfChange(bubbleMode, committed);
        });
        /** Called by child to tell its parent that it has changed. */
        this._additionalProductHasChanged = (freshRef, bubbleMode, committed) => __awaiter(this, void 0, void 0, function* () {
            const i = this.additionalProducts.findIndex((a) => a.isBackedBySame(freshRef));
            if (i !== -1) {
                // Child additional product might not be found. This probably means that propagate
                // has chopped the branch your on. Let's say we have products A -> B -> C.
                // We change an option on C. This is propagated to product A. Product A now
                // changes its additional product so that B is no longer a child of A. Hence
                // C no longer is part of the tree. Odd, but fully permitted.
                this.additionalProducts[i] = freshRef;
            }
            yield this._childHasChanged(bubbleMode, committed);
        });
        /** Called by the configuration to tell its parent that it has changed. */
        this._configurationHasChanged = (freshRef, bubbleMode, committed) => __awaiter(this, void 0, void 0, function* () {
            this._configuration = freshRef;
            switch (bubbleMode) {
                case ProductConfigurationBubbleMode.ValidateAndBubbleSelected:
                    // The revalidate call will continue the bubble
                    yield this._revalidate(CfgProductBubbleMode.ToRootAndBubbleSelected, this._productLoaderRaw, committed);
                    return;
                case ProductConfigurationBubbleMode.BubbleSelected:
                    yield this._childHasChanged(CfgProductBubbleMode.ToRootAndBubbleSelected, committed);
                    return;
                case ProductConfigurationBubbleMode.Validate:
                    // The revalidate call will continue the bubble
                    yield this._revalidate(CfgProductBubbleMode.ToRoot, this._productLoaderRaw, committed);
                    return;
                case ProductConfigurationBubbleMode.ToParentProduct:
                    // Do not continue bubble as we have reached the parent CfgProduct
                    return;
                case ProductConfigurationBubbleMode.OneLevel:
                    yield this._childHasChanged(CfgProductBubbleMode.OneLevel, committed);
                    return;
                case ProductConfigurationBubbleMode.Stop:
                    yield this._childHasChanged(CfgProductBubbleMode.Stop, committed);
                    return;
                case ProductConfigurationBubbleMode.ToRoot:
                    yield this._childHasChanged(CfgProductBubbleMode.ToRoot, committed);
                    return;
            }
        });
        this.getDtoConf = (include) => {
            var _b;
            const conf = {};
            const features = this.configuration._internal.getDtoConf((include & CfgProdConfParts.ExtendedData) === CfgProdConfParts.ExtendedData);
            if (0 < features.length) {
                conf.features = features;
            }
            if ((include & CfgProdConfParts.ProdParams) === CfgProdConfParts.ProdParams) {
                conf.prodParams = this.prodParams;
            }
            // Only store the syncGroupState for the root product. The same state
            // is used for the entire product, including the additional product,
            // so no need to store it for additional products.
            if ((include & CfgProdConfParts.SyncGroupState) === CfgProdConfParts.SyncGroupState &&
                this.parent === undefined) {
                conf.syncGroupState = (_b = this.syncGroupHandler) === null || _b === void 0 ? void 0 : _b.getCompactSyncGroupState();
            }
            const additionalProducts = this.additionalProducts;
            if (0 < additionalProducts.length) {
                conf.additionalProducts = additionalProducts.map((p) => p._internal.getDtoConf(include));
            }
            if (this.isAdditionalProduct) {
                const refKey = this.refKey;
                if (refKey === undefined) {
                    throw new Error("AdditionalProduct without refKey, this should not happen");
                }
                const confAddProd = conf;
                confAddProd.refKey = refKey;
                confAddProd.selected = this.selected;
            }
            return conf;
        };
        this.setDtoConf = (s, doValidate, productLoaderForGroupedLoad) => __awaiter(this, void 0, void 0, function* () {
            // The DtoProdConf format can contain a sync group state, but
            // not the DtoAdditionalProductConfiguration format. So we apply
            // any passed state here.
            if (this.root === this) {
                const syncGroupHandler = this.syncGroupHandler;
                const newSyncGroupState = s.syncGroupState;
                if (syncGroupHandler !== undefined && newSyncGroupState !== undefined) {
                    syncGroupHandler.setCompactSyncGroupState(newSyncGroupState);
                }
            }
            return yield this.setApiSelection(convertDtoProductConfToV1(s), doValidate, productLoaderForGroupedLoad);
        });
        this.setApiSelection = (s, doValidate, productLoaderForGroupedLoad) => __awaiter(this, void 0, void 0, function* () {
            return yield this._setApiSelectionWithOtherProduct(s, doValidate, productLoaderForGroupedLoad, undefined);
        });
        this.copyFrom = (source, doValidate, productLoaderForGroupedLoad) => __awaiter(this, void 0, void 0, function* () {
            return yield this._setApiSelectionWithOtherProduct(convertDtoProductConfToV1(source.getDtoConf(CfgProdConfParts.NoExtra)), doValidate, productLoaderForGroupedLoad, source);
        });
        this._setApiSelectionWithOtherProduct = (productConfiguration, doValidate, productLoaderForGroupedLoad, sourceProduct) => __awaiter(this, void 0, void 0, function* () {
            // Wrap with cache will make getProduct for this function call use the same server call
            // for the same product with the same params. Used for getProduct (when a new additional
            // product is loaded) and postValidate.
            productLoaderForGroupedLoad =
                productLoaderForGroupedLoad || wrapWithCache(this._productLoaderRaw);
            let change = false;
            if (sourceProduct !== undefined) {
                if (!compareCfgProductData(this._rawProductData, sourceProduct.rawProductData)) {
                    this._setRawProductData(sourceProduct.rawProductData);
                    change = true;
                }
                if (this.configuration._internal.addRawFeatures(sourceProduct.configuration.rawFeatures, false)) {
                    change = true;
                }
                if (this.configuration._internal.populateFeatures(sourceProduct.configuration.rootFeatureRefs)) {
                    change = true;
                }
                const targetNotes = this._notes;
                const sourceNotes = sourceProduct._notes;
                for (const targetKey of targetNotes.keys()) {
                    if (!sourceNotes.has(targetKey)) {
                        targetNotes.delete(targetKey);
                        change = true;
                    }
                }
                for (const [sourceKey, sourceNote] of sourceNotes) {
                    if (!targetNotes.has(sourceKey)) {
                        targetNotes.set(sourceKey, sourceNote);
                        change = true;
                    }
                }
            }
            const configurationChange = yield this.configuration._internal.setApiSelection(productConfiguration.selOptions, sourceProduct === null || sourceProduct === void 0 ? void 0 : sourceProduct._rawProductData.partsData.constrOptions, false);
            if (configurationChange) {
                change = true;
            }
            if (this.optional) {
                if (yield this.setSelected(productConfiguration.selected !== false, CfgProductBubbleMode.Stop, false)) {
                    change = true;
                }
            }
            yield this._syncAndLoadAdditionalProducts(productLoaderForGroupedLoad, productConfiguration);
            const apiSelectionAdditionalProducts = productConfiguration.additionalProducts || [];
            const additionalProducts = this.additionalProducts.slice();
            const additionalProductsCount = additionalProducts.length;
            const apiSelectionAdditionalProductsCount = apiSelectionAdditionalProducts.length;
            if (apiSelectionAdditionalProductsCount !== additionalProductsCount) {
                throw new Error(`Additional products are not same length. This product: "${this.key}". This product additional products count: ${additionalProductsCount}. Passed apiSelection additional products count: ${apiSelectionAdditionalProductsCount}`);
            }
            const sourceProductAdditionalProducts = sourceProduct === null || sourceProduct === void 0 ? void 0 : sourceProduct.additionalProducts;
            assert(!sourceProductAdditionalProducts ||
                additionalProductsCount === sourceProductAdditionalProducts.length, `Passed sourceProduct does not have the same number of additional products as this.`);
            if ((yield Promise.all(apiSelectionAdditionalProducts.map((apiSelectionAdditionalProduct) => __awaiter(this, void 0, void 0, function* () {
                var _c;
                const refKey = apiSelectionAdditionalProduct.refKey;
                assertDefined(refKey, "Additional product api configurations must have refKey.");
                const i = additionalProducts.findIndex((a) => refKey === a.refKey);
                assert(i !== -1, `Additional product not found. This product: "${this.key}". refKey of not found additional product: "${refKey}"`);
                let sourceProductAdditionalProduct = undefined;
                if (sourceProductAdditionalProducts !== undefined) {
                    sourceProductAdditionalProduct =
                        (_c = sourceProductAdditionalProducts.find((a) => refKey === a.refKey)) === null || _c === void 0 ? void 0 : _c._internal;
                    assertDefined(sourceProductAdditionalProduct, "Additional product not found in sourceProduct");
                }
                const additionalProduct = additionalProducts.splice(i, 1)[0]; // Splicing like this is okay because this is done synchronous. The setCon. is what is async.
                return yield additionalProduct._internal._setApiSelectionWithOtherProduct(apiSelectionAdditionalProduct, doValidate, productLoaderForGroupedLoad, sourceProductAdditionalProduct);
            })))).some((b) => b)) {
                change = true;
            }
            if (doValidate && configurationChange) {
                yield this._revalidate(CfgProductBubbleMode.ToRoot, productLoaderForGroupedLoad, true);
            }
            else if (change) {
                // As setApiSelection is done recursively each level takes care of its own notifications
                // so we only need to bubble one level to notify this and swap out the reference in the
                // parent.
                yield this._notifyAllOfChange(CfgProductBubbleMode.OneLevel, true);
            }
            return change;
        });
        this.structureCompare = (other, strictOrder = true, descriptionMatch = false) => {
            if (!this.configuration.structureCompare(other.configuration, strictOrder, descriptionMatch)) {
                return false;
            }
            return compareArrays(this.additionalProducts, other.additionalProducts, (l, r) => (!descriptionMatch || (descriptionMatch && isDescriptionMatch(l, r))) &&
                l.structureCompare(r, strictOrder, descriptionMatch), !descriptionMatch);
        };
        this.tryMatchSelection = (other, descriptionMatch = false, // Match on case insensitive description, not code
        productLoaderForGroupedLoad) => __awaiter(this, void 0, void 0, function* () {
            // wrap with cache will make getProduct for this function call use the same server call
            // for the same product with the same params
            productLoaderForGroupedLoad =
                productLoaderForGroupedLoad || wrapWithCache(this._productLoaderRaw);
            let change = false;
            if (this.optional && other.optional) {
                if (yield this.setSelected(other.selected, CfgProductBubbleMode.Stop, false)) {
                    change = true;
                }
            }
            const configurationChange = yield this.configuration._internal.tryMatchSelection(other.configuration._internal, descriptionMatch, false);
            if (configurationChange) {
                change = true;
                yield this._revalidate(CfgProductBubbleMode.ToRootAndBubbleSelected, productLoaderForGroupedLoad, true);
            }
            else if (change) {
                yield this._notifyAllOfChange(CfgProductBubbleMode.ToRootAndBubbleSelected, true);
            }
            const thisAdditionalProducts = this.additionalProducts;
            const otherAdditionalProducts = other.additionalProducts;
            const promises = [];
            if (descriptionMatch) {
                for (const otherAdditionalProduct of otherAdditionalProducts) {
                    if (1 <
                        count(otherAdditionalProducts, (product) => isDescriptionMatch(product, otherAdditionalProduct))) {
                        console.warn("tryMatchSelection will ignore products that have the same description");
                        continue;
                    }
                    const toTryMatchProducts = thisAdditionalProducts.filter((product) => isDescriptionMatch(product, otherAdditionalProduct));
                    if (1 < toTryMatchProducts.length) {
                        console.warn("tryMatchSelection will ignore products that have the same description");
                        continue;
                    }
                    if (toTryMatchProducts.length === 0) {
                        continue;
                    }
                    promises.push(toTryMatchProducts[0]._internal.tryMatchSelection(otherAdditionalProduct._internal, descriptionMatch, productLoaderForGroupedLoad));
                }
            }
            else {
                if (thisAdditionalProducts.length !== otherAdditionalProducts.length) {
                    console.warn("tryMatchSelection not same count, will not try to match further.");
                    return change;
                }
                for (let i = 0; i < thisAdditionalProducts.length; i++) {
                    promises.push(thisAdditionalProducts[i]._internal.tryMatchSelection(otherAdditionalProducts[i]._internal, descriptionMatch, productLoaderForGroupedLoad));
                }
            }
            if ((yield Promise.all(promises)).some((b) => b)) {
                change = true;
            }
            return change;
        });
        /** Only features in selected options and selected additional products. */
        this._getDescendantFeaturesWithCode = (code) => this.additionalProducts.reduce((agg, additionalProduct) => {
            if (additionalProduct.selected) {
                agg.push(...additionalProduct._internal._getDescendantFeaturesWithCode(code));
            }
            return agg;
        }, this._configuration._internal._getFeaturesWithCode(code));
        /**
         * Do a validate call for this product. It does not validate additional products, only this
         * product in isolation. The validation result is applied on the configuration. Then additional
         * products are synced (unloaded, loaded etc.) Finally the changes bubble up the tree.
         */
        this._revalidate = (bubbleMode, productLoader, committed) => __awaiter(this, void 0, void 0, function* () {
            const { _configuration: configuration } = this;
            const token = this.loadingObservable.startChildLoading();
            this._revalidateInProgressToken = token;
            try {
                const response = yield productLoader.postValidate(correctDefaultsOnCatalogueParams(this.prodParams), {
                    selOptions: configuration._internal.getApiSelection(),
                    knownFeatureCodes: configuration.rawFeatures.map((f) => f.code),
                });
                // The revalidateInProgressToken is used to know if some other revalidate
                // call has happened after this call, thereby making this call obsolete.
                // This is a bit crude in that it does not actually cancel previous validate
                // calls, but this is fine because 1. it is hard to cancel a call, especially
                // if you wanna have clear code 2. the actual calls are small anyhow 3. most
                // of all, the heavy work happens on the server, and that work will not be
                // cancelled even if we would cancel the call.
                if (this._revalidateInProgressToken !== token) {
                    return false;
                }
                // After a successful validate-call we will always assume there
                // is a change. It would be possible to compare relevant parts
                // of productData, consider the result of setApiSelection and
                // syndAndLoad, however the code comparing productData would be fragile
                // and likely to break if new data-fields were added.
                if (this._destroyed) {
                    return false;
                }
                const { productData, rootFeatureRefs, features, notes } = response;
                if (notes !== undefined) {
                    this.addNotes(notes.values());
                }
                this._setRawProductData(productData);
                configuration._internal.addRawFeatures(features, true);
                if (rootFeatureRefs !== undefined) {
                    configuration._internal.populateFeatures(rootFeatureRefs);
                }
                yield configuration._internal.setApiSelection(productData.partsData.selOptions || [], productData.partsData.constrOptions, false);
                yield this._syncAndLoadAdditionalProducts(productLoader, undefined);
                if (this._destroyed) {
                    return false;
                }
                yield this._notifyAllOfChange(bubbleMode, committed);
                return true;
            }
            catch (e) {
                throw augmentErrorMessage(e, `Validate product configuration request (${this.prodParams.partNumber}) failure`);
            }
            finally {
                this.loadingObservable.stopChildLoading(token);
            }
        });
        /**
         * Based on this configuration find what additional products should be shown and not, unload
         * (i.e. destroy) those that should no longer be shown, load the new ones.
         */
        this._syncAndLoadAdditionalProducts = (productLoaderForGroupedLoad, initialProductConfiguration) => __awaiter(this, void 0, void 0, function* () {
            const { _productLoaderRaw: productLoaderRaw, rawProductData: productData, configuration, additionalProducts: currentAdditionalProducts, } = this;
            const additionalProductRefs = [
                ...(productData.additionalProductRefs || []),
                ...collectAdditionalProductRefs(configuration),
            ]
                .reduce((a, c) => {
                if (a.every((p) => !isSameProductRef(p, c))) {
                    a.push(c);
                }
                return a;
            }, [])
                .map((prodRef, originalIndex) => ({
                prodRef,
                originalIndex,
            }));
            let change = false;
            let i = currentAdditionalProducts.length;
            while (i--) {
                const currentAdditionalProduct = currentAdditionalProducts[i];
                const j = additionalProductRefs.findIndex((p) => {
                    const prodRef = p.prodRef;
                    return (prodRef.refKey === currentAdditionalProduct.refKey &&
                        prodRef.partNumber === currentAdditionalProduct.partNumber &&
                        isSameCatalogueParams(prodRef.catId, currentAdditionalProduct.catId));
                });
                if (j === -1) {
                    currentAdditionalProduct.destroy();
                    currentAdditionalProducts.splice(i, 1);
                    change = true;
                }
                else {
                    currentAdditionalProduct._internal._updateAdditionalProdRef(additionalProductRefs[j].prodRef);
                    additionalProductRefs.splice(j, 1);
                }
            }
            const token = this.loadingObservable.startChildLoading();
            try {
                if (additionalProductRefs.length !== 0) {
                    change = true;
                }
                const newAdditionalProducts = yield Promise.all(additionalProductRefs.map((p) => (() => __awaiter(this, void 0, void 0, function* () {
                    var _d;
                    const additionalProductRef = p.prodRef;
                    const refKey = additionalProductRef.refKey;
                    const additionalProductInitialProductConfiguration = (_d = initialProductConfiguration === null || initialProductConfiguration === void 0 ? void 0 : initialProductConfiguration.additionalProducts) === null || _d === void 0 ? void 0 : _d.find((aP) => refKey === aP.refKey);
                    if (initialProductConfiguration !== undefined &&
                        additionalProductInitialProductConfiguration === undefined) {
                        console.warn(`Did not find initial product configuration for additional product with refKey ${refKey}`);
                    }
                    return {
                        originalIndex: p.originalIndex,
                        product: CfgProduct._makeNewRefFrom(yield _CfgProductInternal.make(productLoaderRaw, productLoaderForGroupedLoad, Object.assign(Object.assign({}, additionalProductRef.catId), { cid: this.prodParams.cid, lang: this.prodParams.lang, partNumber: additionalProductRef.partNumber }), this.settings, additionalProductRef.optional === true, this.loadingObservable, this, this.root, additionalProductRef, additionalProductInitialProductConfiguration)),
                    };
                }))()));
                if (this._destroyed) {
                    return change;
                }
                for (const newAdditionalProduct of newAdditionalProducts) {
                    currentAdditionalProducts.splice(newAdditionalProduct.originalIndex, 0, newAdditionalProduct.product);
                }
                return change;
            }
            finally {
                this.loadingObservable.stopChildLoading(token);
            }
        });
        this._setRawProductData = (productData) => {
            this._rawProductData = productData;
            this._clearStretchMeasurements();
        };
        this.root = root !== null && root !== void 0 ? root : this;
        this.key = makeProductKey(Object.assign(Object.assign({}, prodParams), { partNumber: (_b = _additionalProductRef === null || _additionalProductRef === void 0 ? void 0 : _additionalProductRef.refKey) !== null && _b !== void 0 ? _b : prodParams.partNumber }));
        this._selected = optional ? selected : undefined;
        this.isAdditionalProduct = _additionalProductRef !== undefined;
        if (notes !== undefined) {
            this.addNotes(notes);
        }
        this._configuration = CfgProductConfiguration.make(initSuccess, initFail, rootFeatureRefs, rawFeatures, apiSelection, apiConstraints, this, this.root);
    }
    get selected() {
        return this._selected !== false;
    }
    /**
     * Please note that cloning an additional product will make the clone believe is is
     * an additional product, even if it has no parent and root.
     * Providing the parent and root of what you clone as arguments is unwise as it will
     * make changes you do on the clone be propagated up to the original non-clone root product.
     */
    clone(parent, root) {
        return __awaiter(this, void 0, void 0, function* () {
            const product = yield new Promise((initSuccess, initFail) => {
                var _b;
                const p = new _CfgProductInternal(() => {
                    initSuccess(p);
                }, initFail, this._productLoaderRaw, this.prodParams, this.settings, this.optional, this.selected, this._configuration.rootFeatureRefs, this._configuration.rawFeatures, this._notes.values(), this.uuid, this._rawUnit, this._rawProductData, this.configuration._internal.getApiSelection(), this.configuration._internal.getApiConstrained(), new AggregatedLoadingObservable(), parent, root, this._additionalProductRef, (_b = this._syncGroupHandler) === null || _b === void 0 ? void 0 : _b.clone());
            });
            for (const additionalProduct of this.additionalProducts) {
                product.additionalProducts.push(CfgProduct._makeNewRefFrom(yield additionalProduct._internal.clone(product, root || product)));
            }
            return product;
        });
    }
    /**
     * Internal use. Used when this product is an additional product, and
     * changing a parent product has made the settings for this product
     * change.
     */
    _updateAdditionalProdRef(p) {
        this._additionalProductRef = p;
        if (p.optional !== this.optional) {
            this._selected = p.optional ? false : undefined;
        }
    }
    /**
     * Return a DtoNode using noteRef as a key.
     * Throws an error if no note is found.
     */
    getNote(noteRef) {
        const note = this._notes.get(noteRef);
        if (note === undefined) {
            throw new Error(`Note with noteRef ${noteRef} not found.`);
        }
        return note;
    }
    /**
     * noteRefs is a list of keys coming from CfgOption, CfgFeature or CfgProduct.
     * The keys are used to get a DtoNote[] from notes at CfgProduct.
     */
    getNotes(noteRefs) {
        return noteRefs.map((noteRef) => this.getNote(noteRef));
    }
    addNotes(notes) {
        for (const note of notes) {
            const code = note.code;
            if (!code) {
                throw new Error("Note with no code");
            }
            this._notes.set(code, note);
        }
    }
    get notes() {
        var _b;
        return this.getNotes((_b = this.rawProductData.noteRefs) !== null && _b !== void 0 ? _b : []);
    }
    get miscFiles() {
        var _b;
        return (_b = this._rawProductData.miscFiles) !== null && _b !== void 0 ? _b : [];
    }
    get hasRootFeaturesChanged() {
        return (this._configuration._internal.hasRootFeaturesChanged ||
            this.additionalProducts.some((p) => p._internal.hasRootFeaturesChanged));
    }
    get description() {
        var _b, _c;
        return (_c = (_b = this._additionalProductRef) === null || _b === void 0 ? void 0 : _b.refDescription) !== null && _c !== void 0 ? _c : this._rawProductData.description;
    }
    get rootNodeSources() {
        return this._rawProductData.models;
    }
    get mtrlApplications() {
        return this._rawProductData.mtrlApplications;
    }
    get currency() {
        return this._rawProductData.partsData.currency;
    }
    get fractionDigits() {
        return this._rawProductData.partsData.rounding || 0;
    }
    get prices() {
        return this._rawProductData.partsData.prices;
    }
    get measureDefinitions() {
        var _b;
        if (this._measureDefinitions === undefined) {
            this._measureDefinitions = ((_b = this._rawProductData.measurements) !== null && _b !== void 0 ? _b : [])
                .map((m) => CfgMeasureDefinition.make(m))
                .filter((m) => m !== undefined);
        }
        return this._measureDefinitions;
    }
    get refKey() {
        var _b;
        return (_b = this._additionalProductRef) === null || _b === void 0 ? void 0 : _b.refKey;
    }
    get transform() {
        var _b;
        return (_b = this._additionalProductRef) === null || _b === void 0 ? void 0 : _b.transform;
    }
    get anchor() {
        var _b;
        return (_b = this._additionalProductRef) === null || _b === void 0 ? void 0 : _b.anchor;
    }
    /** @throws an error if the actual unit sent by the server was not a LengthUnit */
    get unit() {
        if (this._unit === undefined) {
            this._unit = toLengthUnit(this._rawUnit);
        }
        return this._unit;
    }
    get aggregatedPrice() {
        const { currency, fractionDigits, rawProductData } = this;
        const { partsData } = rawProductData;
        let { listPrice, basePrice } = partsData;
        if (this._selected === false) {
            return { basePrice: 0, listPrice: 0, currency, fractionDigits };
        }
        for (const additionalProduct of this.additionalProducts || []) {
            const { basePrice: additionalBasePrice, listPrice: additionalListPrice, currency: additionalCurrency, fractionDigits: additionalFractionDigits, } = additionalProduct.aggregatedPrice;
            basePrice += additionalBasePrice;
            listPrice += additionalListPrice;
            if (currency !== additionalCurrency) {
                // This should not be possible
                // The server shouldn't return additional products with different currency from their parent
                throw new Error(`Currency mismatch between parent product and additional product. Parent product: "${this.key}" Additional product: "${additionalProduct.key}". Currency on parent product: ${currency}. Currency on additional product: ${additionalCurrency}`);
            }
            if (fractionDigits !== additionalFractionDigits) {
                // This should not be possible
                // The server shouldn't return additional products with different fraction digits from their parent
                throw new Error(`Fraction digits mismatch between parent product and additional product. Parent product: "${this.key}" Additional product: "${additionalProduct.key}". Fraction digits on parent product: ${fractionDigits}. Fraction digits on additional product: ${additionalFractionDigits}`);
            }
        }
        return { basePrice, listPrice, currency, fractionDigits };
    }
    get optional() {
        return this._selected !== undefined;
    }
    setSelected(selected, bubbleMode, interactive) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.optional) {
                console.warn("This product is not optional. Nothing will happen");
                return false;
            }
            if (this._selected === selected) {
                return false;
            }
            // Vitally important that this happens before the call to reset,
            // so that the reset won't cause infinite loops
            this._selected = selected;
            if (interactive) {
                if (selected) {
                    const syncGroupHandler = this.syncGroupHandler;
                    if (syncGroupHandler !== undefined) {
                        yield syncGroupHandler.init(this.root, wrapWithCache(this._productLoaderRaw));
                    }
                }
                else {
                    yield this.reset();
                    // In case a passed initial configuration has made it reset to selected
                    this._selected = selected;
                }
            }
            yield this._notifyAllOfChange(bubbleMode, true);
            return true;
        });
    }
    get configuration() {
        return this._configuration;
    }
    get rawProductData() {
        return this._rawProductData;
    }
    /**
     * Please note that this relates to the visibility in the Configuration tree.
     * It does not affect the visibility of anything in the 3D view at all.
     */
    get visibleIfAdditionalProduct() {
        return this.rawProductData.hideIfAdditionalProduct !== true;
    }
    /**
     * Please note that this relates to the visibility in the Configuration tree.
     * It does not affect the visibility of anything in the 3D view at all.
     */
    get visibleIfMainProduct() {
        return this.rawProductData.hideIfMainProduct !== true;
    }
    /**
     * Please note that this relates to the visibility in the Configuration tree.
     * It does not affect the visibility of anything in the 3D view at all.
     */
    get visible() {
        return this.isAdditionalProduct
            ? this.visibleIfAdditionalProduct
            : this.visibleIfMainProduct;
    }
    /**
     * Clears cached stretch data which depends on the rawProductData.
     * This should be called when rawProductData changes, which may potentially change the measurements.
     */
    _clearStretchMeasurements() {
        this._measureDefinitions = undefined;
        this._configuration.clearStretchMeasurements();
    }
    get syncGroupHandler() {
        return this.root._syncGroupHandler;
    }
    get syncGroupsVerboseLogging() {
        var _b, _c;
        return (_c = (_b = this.syncGroupHandler) === null || _b === void 0 ? void 0 : _b.verboseLogging) !== null && _c !== void 0 ? _c : false;
    }
    /**
     * Set to true to get verbose sync state changes logged to the console.
     */
    set syncGroupsVerboseLogging(v) {
        const syncGroupHandler = this.syncGroupHandler;
        if (syncGroupHandler === undefined) {
            throw new Error("No syncGroupHandler, so can not change log verbosity");
        }
        syncGroupHandler.verboseLogging = v;
    }
}
_a = _CfgProductInternal;
_CfgProductInternal.make = (productLoaderRaw, productLoaderForGroupedLoad, // Used when instantiating the current product
prodParams, settings, optional, loadingObservable, parent, root, additionalProductRef, initialProductConfiguration) => __awaiter(void 0, void 0, void 0, function* () {
    // Wrap with cache will make getProduct for this function call use the same server call
    // for the same product with the same params. Not retained for future calls, only used
    // at this initial load.
    productLoaderForGroupedLoad =
        productLoaderForGroupedLoad || wrapWithCache(productLoaderRaw);
    let syncGroupHandler = undefined;
    // No root means we are root. We only create SyncGroupsHandler if this is the root product
    if (root === undefined) {
        let initialSyncGroupsState = undefined;
        // DtoProductConf supports containing the sync group state, but the old
        // DtoAdditionalProductConfiguration does not
        if (initialProductConfiguration !== undefined &&
            isProductConf(initialProductConfiguration)) {
            initialSyncGroupsState = initialProductConfiguration.syncGroupState;
        }
        syncGroupHandler = SyncGroupsHandler.make(settings.syncGroupsApplyMode, loadingObservable, initialSyncGroupsState);
    }
    try {
        const defaultCorrectedProdParams = correctDefaultsOnCatalogueParams(prodParams);
        let initialProductConfigurationV1 = undefined;
        if (initialProductConfiguration !== undefined) {
            if (isProductConf(initialProductConfiguration)) {
                initialProductConfigurationV1 = convertDtoProductConfToV1(initialProductConfiguration);
            }
            else if (isAdditionalProductConfiguration(initialProductConfiguration)) {
                initialProductConfigurationV1 = initialProductConfiguration;
            }
        }
        const productResponse = yield (initialProductConfigurationV1
            ? productLoaderForGroupedLoad.postValidate(defaultCorrectedProdParams, {
                knownFeatureCodes: [],
                selOptions: initialProductConfigurationV1.selOptions,
            })
            : productLoaderForGroupedLoad.getProduct(defaultCorrectedProdParams));
        const { productData, rootFeatureRefs, features: rawFeatures, notes, uuid, unit, } = productResponse;
        const initiallySelected = !optional || (initialProductConfigurationV1 === null || initialProductConfigurationV1 === void 0 ? void 0 : initialProductConfigurationV1.selected) === true;
        const product = yield new Promise((initSuccess, initFail) => {
            var _b;
            const p = new _CfgProductInternal(() => {
                // We absolutely do not want anyone to assign to this._configuration. So we want that field private.
                // But we can not set the api selection synchronously. And the product configuration needs "this". So we use this callback.
                // Feel free to find a nicer more readable solution :)
                initSuccess(p);
            }, initFail, productLoaderRaw, prodParams, settings, optional, initiallySelected, rootFeatureRefs !== null && rootFeatureRefs !== void 0 ? rootFeatureRefs : [], rawFeatures, notes === null || notes === void 0 ? void 0 : notes.values(), uuid, unit, productData, (_b = productData.partsData.selOptions) !== null && _b !== void 0 ? _b : [], productData.partsData.constrOptions, loadingObservable, parent, root, additionalProductRef, syncGroupHandler);
        });
        yield product._syncAndLoadAdditionalProducts(productLoaderForGroupedLoad, initialProductConfigurationV1);
        product._initialClone = yield product.clone();
        if (syncGroupHandler !== undefined && !initialProductConfiguration) {
            // As syncGroupHandler is only set for root product we know that we will init with root.
            // If we have an initialProductConfiguration we do not run init for the syncGroups.
            // The idea with an initial configuration is that the product should look as it did when
            // the configuration was saved. Not running init does that. Also, the DtoProductConf
            // object can contain a sync state which is then used to populate the sync state.
            yield syncGroupHandler.init(product, productLoaderForGroupedLoad);
        }
        return product;
    }
    catch (e) {
        throw augmentErrorMessage(e, `Load product request (${prodParams.partNumber}) failure`);
    }
});
export class CfgProduct {
    constructor(_internal) {
        this._internal = _internal;
        this.isBackedBySame = (other) => this._internal === other._internal;
        /**
         * Recursively marks this and descendants as destroyed so that late events are ignored
         * correctly. If you destroy one shallow copy of this you destroy all.
         */
        this.destroy = () => this._internal.destroy();
        /** Makes a clone of this. It is disconnected from the original. */
        this.clone = () => __awaiter(this, void 0, void 0, function* () { return CfgProduct._makeNewRefFrom(yield this._internal.clone()); });
        /**
         * Only applicable when this product is optional.
         * Setting this does not cause a validation call as toggling an optional additional product is
         *  assumed to always be legal.
         */
        this.setSelected = (v) => __awaiter(this, void 0, void 0, function* () { return yield this._internal.setSelected(v, CfgProductBubbleMode.ToRootAndBubbleSelected, true); });
        /**
         * Experimental. Additional products lacks descriptions or keys that are suitably for structure
         * compare, so we use strict-order when trying to match the additional products. This makes
         * this method work nicely for different products having pretty much the same child products.
         */
        this.structureCompare = (other, strictOrder = true, descriptionMatch = false) => this._internal.structureCompare(other._internal, strictOrder, descriptionMatch);
        /**
         * Experimental. Additional products lacks descriptions or keys that are suitably for try
         *  match, so we use strict-order when trying to match the additional products. This makes
         *  this method work nicely for different products having pretty much the same child products.
         *  This method does not propagate its selections.
         *  This method will cause validation calls if something change.
         */
        this.tryMatchSelection = (other, descriptionMatch = false // Match on case insensitive description, not code
        ) => __awaiter(this, void 0, void 0, function* () { return yield this._internal.tryMatchSelection(other._internal, descriptionMatch); });
        /**
         * Gets what selections has been made on the product, recursively including product
         * configuration, optional products and additional products. Used when a full view of all
         * selections on a product is needed, such as when doing Render or Export.
         * @deprecated getDtoConf provides a newer format.
         * @see getDtoConf
         */
        this.getApiSelection = () => convertDtoProductConfToV1(this._internal.getDtoConf(CfgProdConfParts.NoExtra), true);
        /**
         * Applies the configuration (selections) in the passed object onto the product recursively
         * including product configuration, optional products and additional products.
         * @param doValidate Makes a server side validation call. These are necessary to ensure that
         *                   the right models are loaded.
         * @deprecated setDtoConf uses a newer format.
         */
        this.setApiSelection = (configuration, doValidate = true) => __awaiter(this, void 0, void 0, function* () { return yield this._internal.setApiSelection(configuration, doValidate); });
        /**
         * A newer alternative version of getApiSelection. This returns the configuration (selections)
         * on the product, recursively including product configuration, optional products and additional
         * products.
         * This version has the following advantages over getApiSelection:
         * - The format is clearer, designed to be readable
         * - Makes less assumptions about the structure in the Product being unchanging over time. In
         *   particular, the Feature codes are included in the data, so that changes to what Features
         *   are used in a Product is less likely to lead to unexpected results.
         * - You can request ExtendedData, ProductParams and/or SyncGroupState to be included in the
         *   result. This extra data is ignored when passed back into the API, but it can be very useful
         *   for external applications.
         * The other version (getApiSelection) has the advantage of using a format directly compatible with the API:s.
         * @param include   Includes extra data which is not an actual part of the configuration
         */
        this.getDtoConf = (include = CfgProdConfParts.NoExtra) => this._internal.getDtoConf(include);
        /**
         * A newer alternative version of setApiSelection.
         * @param doValidate Makes a server side validation call. These are necessary to ensure that
         *                   the right models are loaded.
         */
        this.setDtoConf = (configuration, doValidate = true) => __awaiter(this, void 0, void 0, function* () { return yield this._internal.setDtoConf(configuration, doValidate); });
        this.listenForChange = (l) => this._internal.changeObservable.listen(l);
        this.stopListenForChange = (l) => this._internal.changeObservable.stopListen(l);
        this.stopAllListenForChange = () => this._internal.changeObservable.stopAllListen();
        this.listenForLoading = (l) => this._internal.loadingObservable.listen(l);
        this.stopListenForLoading = (l) => this._internal.loadingObservable.stopListen(l);
        this.stopAllListenForLoading = () => this._internal.loadingObservable.stopAllListen();
    }
    static make(productLoader, prodParams, settings, initialProductConfiguration) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._makeNewRefFrom(yield _CfgProductInternal.make(productLoader, undefined, prodParams, completeSettings(settings), false, new AggregatedLoadingObservable(), undefined, undefined, undefined, initialProductConfiguration));
        });
    }
    /**
     * Makes an object wrapping the passed object. This is not a clone method, it is a method to
     * make a new outer reference. Like a shallow copy. We use this to help frameworks that are
     * build around using equals to detect change.
     */
    static _makeNewRefFrom(source) {
        return new this(source);
    }
    /**
     * A client side only key that should uniquely identify this product amongst other additional
     * products.
     */
    get key() {
        return this._internal.key;
    }
    /**
     * Only used when this product is in additional product.
     * As a product can have multiple instances of the same additional product this key exists.
     * It will be unique amongst child products, but not globally unique.
     */
    get refKey() {
        return this._internal.refKey;
    }
    get notes() {
        return this._internal.notes;
    }
    get miscFiles() {
        return this._internal.miscFiles;
    }
    get prodParams() {
        return this._internal.prodParams;
    }
    get lang() {
        return this._internal.prodParams.lang;
    }
    get catId() {
        const { cid, enterprise, prdCat, prdCatVersion, priceList, vendor } = this._internal.prodParams;
        return {
            cid,
            enterprise,
            prdCat,
            prdCatVersion,
            priceList,
            vendor,
        };
    }
    get partNumber() {
        return this._internal.prodParams.partNumber;
    }
    get isAdditionalProduct() {
        return this._internal.isAdditionalProduct;
    }
    /** Only used when this product is an additional product. Root products are never optional. */
    get optional() {
        return this._internal.optional;
    }
    /**
     * Only applicable when this product is optional. If this product is not optional this is
     * always true.
     */
    get selected() {
        return this._internal.selected;
    }
    /**
     * Please note that this relates to the visibility in the Configuration tree.
     * It does not affect the visibility of anything in the 3D view at all.
     * Visibility affects the Configuration for this Product, but any Additional Products
     * will not be affected.
     */
    get visible() {
        return this._internal.visible;
    }
    // A similar text to the one below exists in global-message-managers.md and should be kept in sync.
    /**
     * Functional selection is a Catalogues feature where selecting Options on Features result in that you
     * "jump" to another Product as a result of the Validate call. You normally do not notice that a functional
     * selection has occurred except for the styleNr changing. Functional selection can change which Features
     * from the original product call are used as root Features. This can in turn affect if serialized
     * configuration can be applied or not.
     *
     * The SDK can currently only apply serialized configuration if the list of root Features has not changed.
     * For this reason, when functional selection has happened, extracting data for external systems might work
     * well, but reapplying back into Stage will probably fail.
     */
    get hasRootFeaturesChanged() {
        return this._internal.hasRootFeaturesChanged;
    }
    get rawProductData() {
        return this._internal.rawProductData;
    }
    get uuid() {
        return this._internal.uuid;
    }
    get unit() {
        return this._internal.unit;
    }
    get sku() {
        return this._internal.rawProductData.sku;
    }
    get styleNr() {
        return this._internal.rawProductData.partsData.styleNr;
    }
    /** An URL. */
    get preview() {
        return this._internal.rawProductData.navImage;
    }
    get description() {
        return this._internal.description;
    }
    get additionalProducts() {
        return this._internal.additionalProducts;
    }
    get configuration() {
        return this._internal.configuration;
    }
    get transform() {
        return this._internal.transform;
    }
    get currency() {
        return this._internal.currency;
    }
    /**
     * If positive the number of fraction digits.
     * If negative rounding (essentially the number of zeros to the right)
     */
    get fractionDigits() {
        return this._internal.fractionDigits;
    }
    get aggregatedPrice() {
        return this._internal.aggregatedPrice;
    }
}
